"use strict";
Object.defineProperty(exports, "__esModule", {
    value: true
});
function _export(target, all) {
    for(var name in all)Object.defineProperty(target, name, {
        enumerable: true,
        get: Object.getOwnPropertyDescriptor(all, name).get
    });
}
_export(exports, {
    get PrescriberCheckSchema () {
        return PrescriberCheckSchema;
    },
    get PrescriptionClinicOptionSchema () {
        return PrescriptionClinicOptionSchema;
    },
    get PrescriptionDetailSchema () {
        return PrescriptionDetailSchema;
    },
    get PrescriptionPatientSnapshotSchema () {
        return PrescriptionPatientSnapshotSchema;
    }
});
const _zod = require("zod");
const _prescriptionallergycheck = require("./prescription-allergy-check");
const _prescriptionitem = require("./prescription-item");
const _prescriptionstatus = require("./prescription-status");
const _prescriptiontext = require("./prescription-text");
const _clinicprofile = require("../branches/clinic-profile");
const _prescribersnapshot = require("../prescribers/prescriber-snapshot");
/** `RX-` and the sequence number, zero-padded to at least six digits. */ const RX_NO_PATTERN = /^RX-\d{6,}$/;
const PrescriptionPatientSnapshotSchema = _zod.z.object({
    mrn: _zod.z.string().min(1),
    fullName: _zod.z.string().min(1),
    birthDate: _zod.z.iso.date(),
    sex: _zod.z.enum([
        'F',
        'M'
    ])
}).strict();
const PrescriberCheckSchema = _zod.z.enum([
    'ok',
    'missing',
    'expired'
]);
const PrescriptionClinicOptionSchema = _zod.z.object({
    id: _zod.z.string().uuid(),
    name: _zod.z.string(),
    profile: _clinicprofile.ClinicProfileSchema
}).strict();
function requireNull(ctx, value, path, message) {
    if (value !== null) {
        ctx.addIssue({
            code: 'custom',
            path: [
                path
            ],
            message
        });
    }
}
function requireSet(ctx, value, path, message) {
    if (value === null) {
        ctx.addIssue({
            code: 'custom',
            path: [
                path
            ],
            message
        });
    }
}
/** The fields each status fixes. Messages name fields only, never values. */ function refineDetail(value, ctx) {
    value.items.forEach((item, index)=>{
        if (item.position !== index + 1) {
            ctx.addIssue({
                code: 'custom',
                path: [
                    'items',
                    index,
                    'position'
                ],
                message: 'Items must be numbered 1, 2, 3 … in order.'
            });
        }
    });
    const hasAcknowledgements = 'acknowledgements' in value.allergyCheck;
    if (value.status === 'draft') {
        requireNull(ctx, value.rxNo, 'rxNo', 'A draft has no Rx number.');
        requireNull(ctx, value.issuedAt, 'issuedAt', 'A draft has no issue time.');
        requireNull(ctx, value.gamotUpsc, 'gamotUpsc', 'A draft has no Gamot reference.');
        if (hasAcknowledgements) {
            ctx.addIssue({
                code: 'custom',
                path: [
                    'allergyCheck'
                ],
                message: 'A draft carries the live allergy check.'
            });
        }
    } else {
        requireSet(ctx, value.rxNo, 'rxNo', 'An issued prescription has an Rx number.');
        requireSet(ctx, value.issuedAt, 'issuedAt', 'An issued prescription has an issue time.');
        requireSet(ctx, value.prescriber, 'prescriber', 'An issued prescription has a prescriber.');
        requireSet(ctx, value.clinic, 'clinic', 'An issued prescription has a clinic.');
        if (!hasAcknowledgements) {
            ctx.addIssue({
                code: 'custom',
                path: [
                    'allergyCheck'
                ],
                message: 'An issued prescription carries the stored allergy check.'
            });
        }
        if (value.prescriberCheck !== 'ok') {
            ctx.addIssue({
                code: 'custom',
                path: [
                    'prescriberCheck'
                ],
                message: 'An issued prescription passed the prescriber check.'
            });
        }
        if (value.clinicOptions.length > 0) {
            ctx.addIssue({
                code: 'custom',
                path: [
                    'clinicOptions'
                ],
                message: 'An issued prescription has no clinic options.'
            });
        }
    }
    if (value.status === 'voided') {
        requireSet(ctx, value.voidedAt, 'voidedAt', 'A voided prescription has a void time.');
        requireSet(ctx, value.voidReasonCode, 'voidReasonCode', 'A voided prescription has a reason.');
    } else {
        requireNull(ctx, value.voidedAt, 'voidedAt', 'Only a voided prescription has a void time.');
        requireNull(ctx, value.voidReasonCode, 'voidReasonCode', 'Only a voided prescription has a reason.');
        requireNull(ctx, value.voidNote, 'voidNote', 'Only a voided prescription has a void note.');
    }
}
const PrescriptionDetailSchema = _zod.z.object({
    id: _zod.z.string().uuid(),
    encounterId: _zod.z.string().uuid(),
    patientId: _zod.z.string().uuid(),
    prescriberUserId: _zod.z.string().uuid(),
    status: _prescriptionstatus.PrescriptionStatusSchema,
    version: _zod.z.number().int().min(1),
    rxNo: _zod.z.string().regex(RX_NO_PATTERN, {
        message: 'Invalid Rx number.'
    }).nullable(),
    branchId: _zod.z.string().uuid().nullable(),
    items: _zod.z.array(_prescriptionitem.PrescriptionItemSchema).max(_prescriptionstatus.PRESCRIPTION_LIMITS.itemsMax),
    allergyCheck: _zod.z.union([
        _prescriptionallergycheck.PrescriptionIssuedAllergyCheckSchema,
        _prescriptionallergycheck.PrescriptionAllergyCheckSchema
    ]),
    prescriber: _prescribersnapshot.PrescriberSnapshotSchema.nullable(),
    prescriberCheck: PrescriberCheckSchema,
    ptrMissing: _zod.z.boolean(),
    clinic: _clinicprofile.ClinicProfileSchema.nullable(),
    clinicOptions: _zod.z.array(PrescriptionClinicOptionSchema),
    patient: PrescriptionPatientSnapshotSchema,
    issuedAt: (0, _prescriptiontext.isoDateTime)().nullable(),
    voidedAt: (0, _prescriptiontext.isoDateTime)().nullable(),
    voidReasonCode: _prescriptionstatus.PrescriptionVoidReasonSchema.nullable(),
    voidNote: _zod.z.string().nullable(),
    gamotUpsc: _zod.z.string().nullable(),
    createdAt: (0, _prescriptiontext.isoDateTime)(),
    updatedAt: (0, _prescriptiontext.isoDateTime)()
}).strict().superRefine(refineDetail);
